/**
 * @file THE PHONE — the ring when a job comes up, the call that offers it, and
 * the pocket screen the player carries: the mission list on one tab, one row of
 * FREE handgun ammunition on the other, and the resupply drone that brings it.
 *
 * THE MODEL ONLY. It owns a queue, a log, two tokens and a cursor. It draws
 * nothing, it holds no mission list of its own, it has never heard of a portrait
 * and it does not know what a drone looks like.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO THREE.JS, NO ENGINE, NO GAME — ZERO IMPORTS
 * ─────────────────────────────────────────────────────────────────────────────
 * The `Wallet.js` / `Weapons.js` mould, taken to its end: this file imports
 * NOTHING AT ALL. Everything it talks to — `bus`, `weapons`, `drone`, `player`,
 * `interiors` — is INJECTED and **every one of them may be null**. A phone built
 * with five nulls is a legal system that never rings, never orders and never
 * throws, which is what lets `tests/phone.test.mjs` drive every branch in node
 * with duck types, no GPU and no browser.
 *
 * ⚠ It also holds NO NUMBERS. There is no tuning table in this file, no timeout,
 * no cadence and no cap, because there is nothing here to tune: every quantity
 * it ever prints is DERIVED (`weapons.ammoPerBox`) or is the length of a queue.
 * A constant in a phone is a constant somebody balances, and there is nothing
 * to balance — the ammunition is free by owner decision and the ring waits for
 * ever. See A RING NEVER EXPIRES below.
 *
 * ⚠ And it draws NO RANDOMNESS. Rule 4 is satisfied by having nothing to
 * satisfy: there is no `Math.random()` here and no seeded stream either, because
 * a queue is a queue and a token is a counter.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ THERE IS NO `update()` AND THERE IS NO STEP SLOT. DO NOT ADD ONE.
 * ─────────────────────────────────────────────────────────────────────────────
 * `Wallet.js` records the same absence in one line — "money does not tick" — and
 * a phone does not tick either. This system is construction, a handful of bus
 * subscriptions and a teardown; every state change in it is caused by something
 * that already happened (a mission was offered, a key was pressed, a row was
 * tapped), and every one of those arrives as a call or an event.
 *
 * The reason it can get away with that is the freeze. BOTH overlays this file
 * can put on screen — the call and the panel — set the loop's time scale to 0
 * (the `Cutscene.jsx` / `ShopPanel.jsx` idiom; measured 0 `Level#update` calls/s
 * open against 102 closed, `docs/shops.md`). So there is no state that can move
 * underneath a live call or an open panel except a teardown, and `dispose()`
 * covers that. A poll added here would run only in the states where it has
 * nothing to see.
 *
 * ⚠ The corollary, and it is the one that bites: **every republish must be
 * SYNCHRONOUS, inside the call that changed something.** A refresh deferred to
 * the next fixed step arrives never. That is `'shop:state'`-inside-`buy()`, one
 * file over, and it is why {@link createPhone}'s `order` and `setView` emit
 * before they return.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ THE PHONE COMPUTES NO DRONE PERMISSION. THIS IS THE MOST IMPORTANT RULE
 * IN THE FILE, AND EVERY READER WILL WANT TO BREAK IT.
 * ─────────────────────────────────────────────────────────────────────────────
 * The order row is NEVER greyed for "the drone is busy". Not on the cooldown,
 * not on `drone.active`, not on `drone.pending`, not indoors, not while the
 * player is dead. There is exactly ONE `can` test in this file and it is about
 * the AMMO POUCH.
 *
 * The temptation is obvious: the drone has a six-second cooldown and two
 * defers, so surely the row should say so. It must not, for two reasons that
 * both hold on their own.
 *
 *  1. **IT WOULD BE A LIE ABOUT THE SYSTEM UNDERNEATH.** `Drone#forceOrder()`
 *     sets `pending` and returns true **even on cooldown, even indoors, even
 *     dead** — it bypasses the reserve test and NOTHING ELSE — and `pending`
 *     SURVIVES both defers, which is the whole indoor answer (`docs/drone.md`).
 *     So a row greyed for "drone busy" would refuse an order that WOULD HAVE
 *     SUCCEEDED: the sortie was going to launch the moment the player stepped
 *     back onto the pavement, and the phone talked them out of asking.
 *  2. **TWO PLACES COMPUTING ONE PERMISSION IS THE `hurtPlayer(amount, lethal)`
 *     FAMILY OF BUG.** This project has paid for that shape more than once —
 *     two callers, two readings of "may this happen", and the disagreement
 *     visible only in the one state nobody tests. `Weapons.js` records the same
 *     rule from the other side: `Gameplay` decides whether a shot is ALLOWED,
 *     the gun decides whether it HAS a round. Here: **the phone orders; the
 *     drone decides when it flies.**
 *
 * What the panel gets instead is a STATUS PILL that STATES rather than gates —
 * `docs/ui.md`'s "a pill states, a hint instructs". {@link createPhone}'s
 * snapshot carries `status`, printed VERBATIM by the UI:
 *
 * ```
 *   status = drone.active ? drone.stateName : drone.pending ? 'ORDERED' : 'READY'
 * ```
 *
 * ⚠ `stateName`, `active` and `pending` ALL ALREADY EXIST on the drone.
 * **`Drone.js` gains nothing this round and must not be edited for this.** The
 * pill tells the player a machine is already in the air; it never stops them
 * asking for another one, and a second order while one is `pending` is
 * idempotent over there anyway.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `available` DROPS THE ROW, `can` GREYS IT — AND THERE IS NO PRICE FIELD
 * ─────────────────────────────────────────────────────────────────────────────
 * The `Shops.js` split, with one row instead of a catalogue:
 *
 * ```
 *   available (row ABSENT — the game cannot deliver it at all)
 *     · no weapons system        → there is no pouch to fill
 *     · no `drone.forceOrder`    → there is nothing to deliver it
 *     · `ammoPerBox <= 0`        → the box is empty
 *   can (row GREYED, with a reason) — THERE IS EXACTLY ONE
 *     · `weapons.roomForAmmo() !== true` → 'Pouch full'
 * ```
 *
 * ⚠ **NO `price`, NO `cost`, AND NO `REFUSAL_RANK`.** A `cost: 'Free'` that is
 * always the string `'Free'` is exactly the pre-building `docs/shops.md` and
 * `docs/drone.md` refuse in five separate places, and a precedence table for a
 * single refusal state is the same mistake wearing a hat: both are structure
 * built for a second case that does not exist and may never. The omissions are
 * DECISIONS. The sentence lives in `detail` and it is DERIVED —
 * `` `Free · ${weapons.ammoPerBox} rounds` `` — so there is still no second copy
 * of 60 anywhere, which is the mirror `Drone.js` closed and this file must not
 * re-open.
 *
 * ⚠ The order asks {@link createWeapons}`.roomForAmmo()` rather than doing
 * arithmetic on a reserve and a cap, for the reason the shop's `ammo` verb
 * records: two places computing "is the pouch full" is the bug the whole
 * one-model rule exists to prevent.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ A RING NEVER EXPIRES — A DECISION, NOT AN OMISSION
 * ─────────────────────────────────────────────────────────────────────────────
 * There is no timer on the queue and there never will be. A call that times out
 * is a story beat the player NEVER SAW, and the player is frequently mid-
 * firefight, mid-chase or three blocks into a drive when a job unlocks. Nothing
 * mechanical is lost by waiting: the mission is takeable at its world giver ring
 * whatever the phone says — **the call OFFERS, the ring STARTS** — so the only
 * thing an expiry could take away is the one thing the feature exists for.
 *
 * The queue therefore has no cap and no dedupe either. It does not need them:
 * `Missions.js` latches `offered` and routes both writers of
 * `MISSION_STATE.AVAILABLE` through one door, so a job is offered at most ONCE
 * per session and the queue grows by at most one per mission. A cap here would
 * be a second bound on a bounded thing, and a dedupe a second latch.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ ONE LOOP-FREEZING OVERLAY AT A TIME, ENFORCED IN THE MODEL
 * ─────────────────────────────────────────────────────────────────────────────
 * `openToken` IS the panel-open flag and `callToken` IS the call-on-screen flag
 * — one variable each, so "is it up" and "which one is up" can never disagree
 * (the `Shops.js` idiom). They are MUTUALLY EXCLUSIVE: `open()` refuses while a
 * call is on screen and `answer()` refuses while the panel is open. That single
 * rule is what keeps this file contributing at most ONE frozen overlay at any
 * instant — three overlays already freeze this loop and the last one to restore
 * the time scale wins (`docs/shops.md`'s registration-order bug).
 *
 * ⚠ Both `close()` and the private cancel clear their token BEFORE they emit,
 * which is what makes this file DEAF TO ITS OWN two-way close: the incoming
 * handler's first test is that the token matches, and by then it does not. That
 * is the structural version of `Cutscene.jsx`'s `sentRef` latch, and it is why
 * one file can own both ends of a channel without a second flag.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THE CALL'S SHOT CURSOR LIVES HERE, NOT IN THE COMPONENT
 * ─────────────────────────────────────────────────────────────────────────────
 * `'phone:call'` is republished once PER SHOT on the same token; the overlay
 * renders one snapshot and asks for the next. That is the `ShopPanel` law — the
 * renderer decides nothing — and it is what lets the `P` key and a tap on the
 * card advance the same call without two cursors that can drift apart.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BUS CONTRACT — FROZEN. Other systems are built against these names.
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 *   engine → ui   'phone:ring'      {waiting, id, name, caller}   ON CHANGE + once at construction
 *   engine → ui   'phone:call'      {token, id, name, caller, speaker, portrait,
 *                                    shot, shots, text, last}
 *   engine → ui   'phone:call:end'  {token, cancelled?}           ⚠ TWO-WAY
 *   engine → ui   'phone:open'      {token, view, status, rows, calls}
 *   engine → ui   'phone:state'     the same payload              ⚠ SYNCHRONOUS
 *   engine → ui   'phone:close'     {token}                       ⚠ TWO-WAY
 *   ui → engine   'phone:show'      {}
 *   ui → engine   'phone:answer'    {}
 *   ui → engine   'phone:ignore'    {}
 *   ui → engine   'phone:call:next' {token}
 *   ui → engine   'phone:call:end'  {token}
 *   ui → engine   'phone:view'      {token, view}   'missions' | 'order'
 *   ui → engine   'phone:order'     {token, id}
 *   ui → engine   'phone:callback'  {token, id}
 *   ui → engine   'phone:close'     {token}
 *   ui → engine   'phone:request'   {}   republish ring (+ state if open) NOW
 * ```
 *
 * • **SOLE PUBLISHER of every `engine → ui` channel above.** The `Wallet.js` /
 *   `'player:cash'` rule, for the `'hud:prompt'` reason: two systems on one
 *   channel means the HUD shows whichever spoke last and the disagreement is a
 *   flicker nobody can reproduce.
 * • It subscribes to exactly TWO ENGINE-side channels: `'mission:offer'` (the
 *   whole ring mechanism) and `'player:wasted'` (which cancels a live call —
 *   cheap, and the safety net if the freeze is ever lifted). ⚠ It does NOT
 *   subscribe to `'mission:list'` and holds no copy of the mission list: the
 *   panel's missions tab reads that channel itself, exactly as the pause menu's
 *   list already does. A second consumer that re-published would be a second
 *   publisher on a channel `Missions.js` owns.
 * • `'phone:ring'` fires ON CHANGE **and once at construction** — the
 *   `Wallet.js` rule. A HUD that waited for the first real ring would render
 *   nothing at all on a level that has a phone. It is never per-step (there is
 *   no step) and never per-frame (the `'drone:state'` rule).
 * • ⚠ `'phone:request'` exists because THE BUS HAS NO REPLAY and the HUD mounts
 *   after `buildLevel` returns. `'mission:list'` already proved that "on change"
 *   and "mounts late" do not mix — found in the browser as an empty list — and
 *   the answer there was a pull. This is the same pull.
 * • Every two-way channel is TOKEN-GATED and rejects a stale token from either
 *   side. ⚠ THE THREE THAT ARE NOT are `'phone:show'`, `'phone:answer'` and
 *   `'phone:ignore'`: they come from the ring banner and the phone button, which
 *   are ordinary non-modal HUD chrome with no overlay and therefore no token to
 *   carry. Each of them routes to a method whose own truth table is the gate.
 * • ⚠ **THE STATUS PILL'S VALUE SET IS CLOSED**: exactly
 *   `'READY' | 'ORDERED' | 'INBOUND' | 'DELIVER' | 'OUTBOUND'` — `'READY'` and
 *   `'ORDERED'` from here, the other three being the drone's own `stateName`
 *   verbatim (it is `active` only outside `IDLE`). The UI prints it with ONE
 *   neutral treatment and no per-value branch, so a sixth value is a change to
 *   announce out loud, not a string to add quietly.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NOT IN THIS ROUND
 * ─────────────────────────────────────────────────────────────────────────────
 * No paid order types (a weapon, a taxi, a hitman) — the row set is one FREE
 * box of handgun ammunition and the shape that would carry a price does not
 * exist yet. No contacts, no text messages, no call history beyond the log a
 * callback needs, no sound, no vibration, and no way for the phone to START a
 * mission: the call OFFERS and the world's giver ring still STARTS, which is an
 * owner decision and not an unfinished edge.
 *
 * @module engine/systems/Phone
 */

/**
 * @typedef {Object} PhoneShot One card of a call. The authored shape is the
 *   cutscene's, minus the image: a speaker and a line.
 * @property {string} text
 * @property {string} [speaker]
 * @property {string} [portrait] Art key. `image` is accepted as an alias, since
 *   `CutsceneDef` spells it that way and a call is authored beside cutscenes.
 * @property {string} [image]
 */

/**
 * @typedef {Object} PhoneCallDef What a mission def puts in its `call` field.
 *   CONTENT — this file never authors one and never reads a field of it that is
 *   not listed here.
 * @property {string} [caller] Who is on the line. Falls back to the first shot's
 *   speaker, then to the empty string.
 * @property {string} [portrait] A call-wide portrait, used for any shot that
 *   does not carry its own.
 * @property {PhoneShot[]} shots At least one with a real `text`, or there is
 *   nothing to say and the call is not queued at all.
 */

/**
 * @typedef {Object} PhoneCall One queued or logged call, as this file holds it.
 * @property {string} id The mission id. STABLE — `'phone:callback'` carries it.
 * @property {string} name The mission's display name.
 * @property {string} caller
 * @property {string|null} portrait
 * @property {PhoneShot[]} shots Normalised: every entry has a real `text`.
 * @property {boolean} missed True once it went to the log unanswered.
 */

/**
 * @typedef {Object} PhoneRow One row of the order screen. The ENTIRE contract
 *   with the panel for that tab: it can draw the row with no knowledge of ammo,
 *   drones or weapons, because every one of those has already been resolved
 *   into a string or a state here.
 * @property {string} id STABLE — `'phone:order'` carries it.
 * @property {string} label
 * @property {string} detail The whole sentence, DERIVED. ⚠ There is deliberately
 *   no `price`/`cost` field — see the header.
 * @property {'ready'|'full'} state
 * @property {string|null} reason `null` for `'ready'`; the refusal otherwise.
 */

/**
 * @typedef {Object} PhoneSnapshot What the panel renders. Never null.
 * @property {string|null} token The open panel's token, or null.
 * @property {string} view `'missions'` or `'order'`.
 * @property {string} status The drone's status PILL — a statement, never a gate.
 * @property {PhoneRow[]} rows Possibly empty. ⚠ The panel STILL OPENS.
 * @property {Array<{id: string, name: string, caller: string, missed: boolean}>} calls
 *   Every call that has already been dealt with, in ARRIVAL order. The renderer
 *   decides which end to draw first; this file does not sort.
 */

/** The two tabs. Strings because they are published straight to React. */
export const PHONE_VIEW = Object.freeze({ MISSIONS: 'missions', ORDER: 'order' });

/**
 * The one order row's STABLE id — it is what `'phone:order'` carries and what
 * {@link createPhone}'s `order(id)` is called with. Renaming it changes the
 * meaning of an event somebody else may already be reading.
 */
const ROW_AMMO = 'ammo';

/** The row's title. */
const LABEL_AMMO = 'Ammo drop';

/**
 * The ONE refusal string in this file. Short on purpose: it prints inside a row
 * on a phone screen, next to a label, on a device that may be a handset.
 */
const REASON_FULL = 'Pouch full';

/** The status pill's two words that are not the drone's own state names. */
const STATUS_ORDERED = 'ORDERED';
const STATUS_READY = 'READY';

/**
 * @param {any} s
 * @returns {boolean} A real, usable, non-empty string. The `Wallet.js#usable`
 *   guard in its string form: a malformed payload must not become a row label
 *   reading `undefined`.
 */
const str = (s) => typeof s === 'string' && s.length > 0;

/**
 * The player's phone: a queue of calls, a log of them, and one screen.
 *
 * @param {Object} [o]
 * @param {any} [o.bus] Every channel in the header. ⚠ Without one NOTHING can
 *   open — the panel and the call are the only two things this system produces,
 *   and an overlay nobody can be told about is an overlay that would never
 *   close. So `open()` and `answer()` both require a bus; that is the promise
 *   rule (`docs/interiors.md`), not a convenience.
 * @param {any} [o.weapons] `engine/systems/Weapons.js`. Read for `ammoPerBox`
 *   (the row's DERIVED sentence) and asked `roomForAmmo()` (the one `can`).
 *   Never given rounds directly — that is the drone's job, and a phone that
 *   called `addAmmo` would be a teleporter.
 * @param {any} [o.drone] `engine/systems/Drone.js`. Called for `forceOrder()`
 *   and READ for `active` / `pending` / `stateName`, which is the whole status
 *   pill. ⚠ Never asked whether it MAY fly — see the header.
 * @param {any} [o.player] For `dead`. A phone is not answered by a corpse.
 * @param {any} [o.interiors] For `locked` — a door transition already running.
 *   ⚠ `plan` is deliberately NOT consulted: see {@link createPhone}'s `open`.
 * @returns {Object}
 */
export function createPhone(o = {}) {
  const {
    bus = null,
    weapons = null,
    drone = null,
    player = null,
    interiors = null,
  } = o ?? {};

  // ── the state ──────────────────────────────────────────────────────────────
  /**
   * THE RING. A FIFO of calls that have arrived and not yet been dealt with;
   * `queue.length` IS the ring, so there is no RINGING byte, no cadence and no
   * timer. ⚠ The parenthetical here used to read "there is no sound in this
   * project either"; there is now (`docs/audio.md`), and it changed NOTHING in
   * this file — `GameAudio.js` subscribes to `'phone:ring'` and turns the
   * `waiting` COUNT into a loop. The phone still owns no cadence. ⚠ No cap, no dedupe and
   * NO EXPIRY — see the header.
   * @type {PhoneCall[]}
   */
  const queue = [];
  /**
   * Every call that has LEFT the queue, answered or missed, in arrival order.
   * This is what makes an ignored call callable back; it is also the only
   * history this file keeps.
   * @type {PhoneCall[]}
   */
  const log = [];

  /**
   * The open panel's token, or null. IT IS THE OPEN FLAG.
   * @type {string|null}
   */
  let openToken = null;
  /**
   * The on-screen call's token, or null. IT IS THE IN-CALL FLAG. ⚠ Mutually
   * exclusive with {@link openToken} — see the header.
   * @type {string|null}
   */
  let callToken = null;
  /** The call being played, while {@link callToken} is set. @type {PhoneCall|null} */
  let callRec = null;
  /**
   * WHICH SHOT IS ON SCREEN. ⚠ The cursor lives here rather than in the React
   * component — the renderer decides nothing, and one cursor is what lets a key
   * and a tap advance the same call.
   */
  let shotIdx = 0;

  /** The open panel's tab. Reset on every open — see {@link open}. */
  let view = PHONE_VIEW.MISSIONS;

  /**
   * ⚠ ONE PLAIN COUNTER FOR BOTH TOKEN KINDS. Rule 4: no `Math.random()`
   * anywhere in `src/`, and a token is not a secret — it exists to reject a
   * STALE answer (the `mission:cutscene:end` idiom), and MONOTONIC is the
   * strongest thing an ordering test can ask for. Two prefixes off one counter
   * means a panel token and a call token can never collide either.
   */
  let seq = 0;

  /**
   * The last `'phone:ring'` signature, so the channel fires ON CHANGE. A string
   * rather than a pair of fields, because "the head changed" and "the depth
   * changed" are both rings and neither is more equal.
   */
  let ringSig = '';

  let disposed = false;

  // ── counters (for `stats()`, `DebugPanel` and the suites) ──────────────────
  /** Calls that arrived. */
  let rang = 0;
  /** Calls answered. */
  let answered = 0;
  /** Calls sent to the log unanswered. */
  let ignored = 0;
  /** Orders that reached the drone. */
  let orders = 0;

  /** @type {Array<() => void>} */
  const unsubs = [];

  // ── the ring ───────────────────────────────────────────────────────────────

  /**
   * ⚠ ON CHANGE, plus once at construction. Called from every place the queue
   * moves, and from `'phone:request'` with `force` — so "never per step" is a
   * property of there being no step, and "never twice for one change" is a
   * property of the signature.
   * @param {boolean} [force] Republish even when nothing moved (the pull).
   */
  const emitRing = (force = false) => {
    if (!bus) return;
    const head = queue[0] ?? null;
    const sig = `${queue.length}|${head ? head.id : ''}`;
    if (!force && sig === ringSig) return;
    ringSig = sig;
    bus.emit?.('phone:ring', {
      waiting: queue.length,
      id: head ? head.id : null,
      name: head ? head.name : null,
      caller: head ? head.caller : null,
    });
  };

  /**
   * One authored shot, normalised, or null. A shot with no real `text` is
   * nothing to say and is dropped — the call is sized to what will actually be
   * drawn, so `shots` in the payload is a count the UI can trust for a progress
   * dot row.
   * @param {any} s
   * @returns {PhoneShot|null}
   */
  const shotOf = (s) => {
    if (!s || typeof s !== 'object' || !str(s.text)) return null;
    return {
      text: s.text,
      speaker: str(s.speaker) ? s.speaker : '',
      // `image` is `CutsceneDef`'s spelling and a call is authored beside
      // cutscenes; accepting both costs one `??` and saves a content trap.
      portrait: str(s.portrait) ? s.portrait : (str(s.image) ? s.image : ''),
    };
  };

  /**
   * A `'mission:offer'` payload, turned into a queued call — or nothing at all.
   *
   * ⚠ THREE ways this returns null, and they are different sentences:
   *   • the payload is MALFORMED (not an object, no id) — ignored outright, and
   *     never read as an empty call;
   *   • the job carries NO `call` — the director published the fact because the
   *     fact is the director's, and a job with no script has nothing to say;
   *   • the job is NOT `critical` — owner decision: only story missions call.
   *     Side work appears on the map and in the list like it always did.
   *
   * @param {any} p
   * @returns {PhoneCall|null}
   */
  const callOf = (p) => {
    if (!p || typeof p !== 'object' || !str(p.id)) return null;
    if (p.critical !== true) return null;
    const def = p.call;
    if (!def || typeof def !== 'object') return null;
    const raw = Array.isArray(def.shots) ? def.shots : [];
    /** @type {PhoneShot[]} */
    const shots = [];
    for (let i = 0; i < raw.length; i++) {
      const s = shotOf(raw[i]);
      if (s) shots.push(s);
    }
    if (shots.length === 0) return null; // a call with no line is not a call
    const name = str(p.name) ? p.name : p.id;
    return {
      id: p.id,
      name,
      caller: str(def.caller) ? def.caller : shots[0].speaker,
      portrait: str(def.portrait) ? def.portrait : null,
      shots,
      missed: false,
    };
  };

  // ── the order row ──────────────────────────────────────────────────────────

  /**
   * @returns {number} Rounds in one delivery, straight off the WEAPON. ⚠ The
   *   only number this file ever prints, and it is derived — there is no second
   *   copy of 60 here, which is the mirror `Drone.js` closed.
   */
  const boxSize = () => {
    const n = weapons?.ammoPerBox;
    return typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  };

  /**
   * @returns {boolean} The game can deliver this row AT ALL. Anything false
   *   here makes the row ABSENT, never greyed — the `Shops.js` forward-compat
   *   idiom: a row on screen is a promise, and one the game cannot keep must
   *   not be made.
   */
  const ammoAvailable = () => !!weapons
    && typeof drone?.forceOrder === 'function'
    && boxSize() > 0;

  /**
   * @returns {PhoneRow[]} The order screen. Freshly derived every time and
   *   never cached — a caller that kept one would be holding a snapshot of a
   *   world that has moved.
   *
   * ⚠ EXACTLY ONE `can` TEST, and it is about the POUCH. There is no cooldown
   * test, no `drone.active` test, no indoors test and no dead test. See the
   * header at length: `forceOrder()` succeeds in every one of those states, so
   * a row greyed for them would refuse an order that would have worked.
   *
   * ⚠ And it ASKS THE MODEL (`roomForAmmo()`) rather than comparing a reserve
   * against a cap — two places computing "is the pouch full" is the bug the
   * whole one-model rule exists to prevent.
   */
  const rows = () => {
    if (!ammoAvailable()) return [];
    const full = weapons.roomForAmmo?.() !== true;
    return [{
      id: ROW_AMMO,
      label: LABEL_AMMO,
      detail: `Free · ${boxSize()} rounds`,
      state: full ? 'full' : 'ready',
      reason: full ? REASON_FULL : null,
    }];
  };

  /**
   * @returns {'READY'|'ORDERED'|'INBOUND'|'DELIVER'|'OUTBOUND'} The status PILL,
   *   and ⚠ **THAT UNION IS THE WHOLE SET** — the UI prints it verbatim under one
   *   neutral treatment, so a sixth value is announced, never added quietly.
   *
   * ⚠ A STATEMENT, NOT A GATE — nothing in
   *   this file branches on it and the row's state never reads it. It is the
   *   answer to "why has nothing arrived yet", and the whole reason the row can
   *   stay honest about permission it does not own.
   */
  const statusOf = () => {
    if (drone?.active) return str(drone.stateName) ? drone.stateName : STATUS_READY;
    if (drone?.pending) return STATUS_ORDERED;
    return STATUS_READY;
  };

  /**
   * @returns {PhoneSnapshot} What the panel draws, re-derived from the live
   *   world. Never null: with no rows at all it is an empty `rows`, because
   *   "the drone cannot deliver anything right now" is a state a renderer can
   *   draw and a null is not.
   */
  const snapshot = () => ({
    token: openToken,
    view,
    status: statusOf(),
    rows: rows(),
    calls: log.map((c) => ({ id: c.id, name: c.name, caller: c.caller, missed: c.missed })),
  });

  /**
   * ⚠ SYNCHRONOUS, and only while a panel is up. See the header: the loop is
   * frozen under the panel, so a refresh deferred to the next step arrives
   * never — and a state emit with no panel to repaint is traffic on a channel
   * whose whole purpose is to repaint one.
   */
  const publishState = () => {
    if (!bus || !openToken) return;
    bus.emit?.('phone:state', snapshot());
  };

  // ── the call ───────────────────────────────────────────────────────────────

  /**
   * Puts the current shot on screen. Called once per shot, on the SAME token —
   * the overlay renders a snapshot and asks for the next one.
   */
  const publishShot = () => {
    if (!bus || !callToken || !callRec) return;
    const s = callRec.shots[shotIdx];
    if (!s) return;
    bus.emit?.('phone:call', {
      token: callToken,
      id: callRec.id,
      name: callRec.name,
      caller: callRec.caller,
      speaker: str(s.speaker) ? s.speaker : callRec.caller,
      portrait: str(s.portrait) ? s.portrait : callRec.portrait,
      shot: shotIdx,
      shots: callRec.shots.length,
      text: s.text,
      last: shotIdx >= callRec.shots.length - 1,
    });
  };

  /**
   * THE ONE PLACE A CALL GOES ON SCREEN — `answer()` and `callBack()` both come
   * through here, so the two cannot grow different rules about tokens, cursors
   * or the panel.
   *
   * ⚠ The token is minted FIRST, so the very first `'phone:call'` already
   * carries it: an overlay that had to wait for a second event to learn its own
   * token could not answer the first one.
   *
   * @param {PhoneCall} rec
   * @returns {boolean}
   */
  const beginCall = (rec) => {
    callRec = rec;
    shotIdx = 0;
    callToken = `call:${++seq}`;
    publishShot();
    return true;
  };

  /**
   * Takes the call DOWN. ⚠ The token is cleared BEFORE the emit, which is what
   * makes our own two-way handler unable to hear us.
   * @param {boolean} cancelled Was it the world that ended it, rather than the
   *   player reading to the end?
   * @returns {boolean} A call was up and is not now.
   */
  const endCallNow = (cancelled) => {
    if (!callToken) return false;
    const token = callToken;
    callToken = null;
    callRec = null;
    shotIdx = 0;
    // ⚠ A cancelled call is NOT re-queued. A queue that refills itself is a
    // phone that nags, and the mission is takeable at its world ring anyway —
    // the log keeps it, and `callBack` is the way back to it.
    // ⚠ `cancelled` is ABSENT on a normal end rather than `false`: the overlay
    // reads it as "the world took this away from me", and a field that is always
    // present is a field a renderer starts branching on.
    bus?.emit?.('phone:call:end', cancelled ? { token, cancelled: true } : { token });
    return true;
  };

  /** Moves the cursor on, or ends the call on the last shot. */
  const advance = () => {
    if (!callToken || !callRec) return false;
    if (shotIdx >= callRec.shots.length - 1) return endCallNow(false);
    shotIdx++;
    publishShot();
    return true;
  };

  // ── the panel ──────────────────────────────────────────────────────────────

  /**
   * THE TRUTH TABLE, in one expression so the five callers of it cannot
   * disagree — the `Shops.js#evaluate` discipline.
   *
   * ⚠ `interiors.plan` IS NOT ON THIS LIST, AND THAT IS DELIBERATE. The drone
   * defers indoors because a delivery cannot fly through 100 m of rock; the
   * PHONE works perfectly well at a shop counter. You order, the sortie is held
   * (`pending` survives a defer), and it lands the moment you step back onto
   * the pavement. Copying the drone's indoor rule here would be the thoughtless
   * kind of consistency — it would take the phone away in the one place the
   * player is standing still with time to read it.
   *
   * `interiors.locked` IS on the list: mid-transition the player is behind a
   * black curtain standing on the entry mat, and a panel opened during the fade
   * is a phone nobody took out.
   *
   * @returns {boolean}
   */
  const canOpen = () => !disposed
    && !!bus
    && !!player
    && !player.dead
    && !interiors?.locked
    && !callToken // ⚠ one loop-freezing overlay at a time
    && !openToken;

  /**
   * Opens the phone. ⚠ **IT OPENS EVEN WITH ZERO ORDER ROWS** — a stronger form
   * of `docs/shops.md`'s inversion, and easier to justify: the phone always has
   * the missions screen, so there is always something behind the button. The
   * shop refuses when it has nothing renderable because there would be nothing
   * behind ITS door; this never has nothing.
   * @returns {boolean} A panel actually opened.
   */
  const open = () => {
    if (!canOpen()) return false;
    // The tab is reset rather than remembered: the mission list is what the
    // phone is FOR, and a phone that reopened on the order screen would hide it
    // behind a row the player already dealt with.
    view = PHONE_VIEW.MISSIONS;
    openToken = `phone:${++seq}`;
    bus.emit?.('phone:open', snapshot());
    return true;
  };

  /**
   * Closes it. Idempotent — a second call is a no-op and emits nothing.
   * ⚠ The token is cleared BEFORE the emit; see the header.
   * @returns {boolean} A panel was open and is not now.
   */
  const close = () => {
    if (!openToken) return false;
    const token = openToken;
    openToken = null;
    bus?.emit?.('phone:close', { token });
    return true;
  };

  const api = {
    name: 'phone',

    // ── the ring ─────────────────────────────────────────────────────────────

    /**
     * Answers the call at the head of the queue.
     *
     * ⚠ Refuses while the PANEL is open — one loop-freezing overlay at a time,
     * enforced here rather than trusted to the caller.
     * @returns {boolean} A call is now on screen.
     */
    answer() {
      if (disposed || !bus) return false;
      if (openToken || callToken) return false;
      if (player?.dead) return false;
      const rec = queue.shift();
      if (!rec) return false;
      rec.missed = false;
      log.push(rec);
      answered++;
      emitRing();
      return beginCall(rec);
    },

    /**
     * Sends the head of the queue to the log UNANSWERED. Mints no token,
     * publishes no call — the only thing that happens is that the ring gets
     * shorter and a missed entry appears in the panel's list.
     * @returns {boolean} There was one to ignore.
     */
    ignore() {
      if (disposed) return false;
      const rec = queue.shift();
      if (!rec) return false;
      rec.missed = true;
      log.push(rec);
      ignored++;
      emitRing();
      publishState(); // the panel's `calls` list just changed
      return true;
    },

    /**
     * Rings a LOGGED call back — the missed-call list's tap target, and the
     * reason a ring never has to expire.
     *
     * ⚠ **IT CLOSES THE PANEL.** A call and the panel are mutually exclusive
     * overlays, and the callback is a request for the CALL; leaving the panel up
     * underneath would be two frozen overlays with one time scale between them.
     * The player therefore sees `'phone:close'` and then `'phone:call'`, in that
     * order, synchronously — and no `'phone:state'`, because there is no longer
     * a panel to repaint.
     *
     * @param {string} id The mission id. The MOST RECENT logged call with that
     *   id wins — a job can only be offered once per session today, so the
     *   choice is unobservable, and "the newest" is the answer that stays right
     *   if that ever changes.
     * @returns {boolean} A call is now on screen.
     */
    callBack(id) {
      if (disposed || !bus || callToken) return false;
      if (player?.dead) return false;
      let rec = null;
      for (let i = log.length - 1; i >= 0; i--) {
        if (log[i].id !== id) continue;
        rec = log[i];
        break;
      }
      if (!rec) return false;
      close();
      return beginCall(rec);
    },

    // ── the call on screen ───────────────────────────────────────────────────

    /**
     * The next card. ⚠ TOKEN-GATED on both sides: a stale token is a call that
     * outlived its own overlay (a double-tap, a teardown, a second call), and
     * advancing on one would skip a shot of the call that is actually up.
     * @param {string} token
     * @returns {boolean} Something happened — a new shot, or the call ending.
     */
    next(token) {
      if (disposed || !callToken || token !== callToken) return false;
      return advance();
    },

    /**
     * Ends the call from the UI side — the "hang up" the overlay offers on the
     * last card, and the answer to the two-way channel.
     * @param {string} token
     * @returns {boolean}
     */
    endCall(token) {
      if (disposed || !callToken || token !== callToken) return false;
      return endCallNow(false);
    },

    // ── the panel ────────────────────────────────────────────────────────────

    open,
    close,

    /**
     * Switches tab. Republishes SYNCHRONOUSLY — the loop is frozen under the
     * panel, so a state deferred to the next step arrives never.
     * @param {string} v {@link PHONE_VIEW}
     * @returns {boolean} The tab moved. A no-op call and an unknown tab are both
     *   silent and change nothing (the `Wallet#spend` rule).
     */
    setView(v) {
      if (disposed || !openToken) return false;
      if (v !== PHONE_VIEW.MISSIONS && v !== PHONE_VIEW.ORDER) return false;
      if (v === view) return false;
      view = v;
      publishState();
      return true;
    },

    /**
     * ORDERS THE DROP. It calls `drone.forceOrder()` and does NOTHING ELSE —
     * no permission arithmetic, no reserve read, no `addAmmo`. See the header:
     * the phone orders, the drone decides when it flies.
     *
     * @param {string} id A row id from {@link rows}. Re-derived here rather than
     *   trusted: an id from a frozen panel is an id from a world that may have
     *   moved (`Shops#buy`'s rule).
     * @returns {boolean} An order is now outstanding.
     */
    order(id) {
      if (disposed) return false;
      const list = rows();
      let row = null;
      for (let i = 0; i < list.length; i++) {
        if (list[i].id !== id) continue;
        row = list[i];
        break;
      }
      // Not a row we are offering, or one we are offering greyed. Either way the
      // panel is showing something this world does not support.
      if (!row || row.state !== 'ready') return false;
      if (drone.forceOrder() !== true) return false;
      orders++;
      // ⚠ SYNCHRONOUS. The drone's `pending` just went true and the status pill
      // above the row is the only thing on screen that says so.
      publishState();
      return true;
    },

    /** @returns {PhoneRow[]} The order screen, re-derived. */
    rows,

    /**
     * THE CONTEXTUAL KEY — one door, the `interiors → shops → carjack` shape.
     * A cascade rather than three bindings, because there is exactly one phone
     * key and what it means is entirely decided by what is already on screen.
     *
     * ```
     *   1. a call is up      → the next card
     *   2. a call is waiting → answer it
     *   3. otherwise         → open the panel
     * ```
     *
     * ⚠ This is the KEY side only. The touch side keeps three explicit zones
     * (answer, ignore, phone), so `docs/weapons.md`'s "TWO tap zones, never one
     * target with two gestures" still holds — a cascade is safe on a key press
     * and a mis-tap generator on a finger.
     * @returns {boolean} Something happened.
     */
    press() {
      if (disposed) return false;
      if (callToken) return advance();
      if (queue.length > 0) return api.answer();
      return open();
    },

    // ── readouts ─────────────────────────────────────────────────────────────

    /** @returns {boolean} There is a call waiting — the HUD's ring gate. */
    get ringing() {
      return queue.length > 0;
    },
    /** @returns {number} How many are waiting. The ring IS the queue depth. */
    get waiting() {
      return queue.length;
    },
    /**
     * @returns {boolean} The panel is up — which also means the sim is frozen.
     *
     * ⚠ **NAMED `panelOpen`, NOT `open`, AND IT HAS TO BE.** `open()` is the
     * METHOD that opens the panel; a getter of the same name would silently
     * REPLACE it in this object literal (last key wins), so `phone.open()` would
     * throw `is not a function` at the first key press. `Shops.js` carries
     * exactly this pair under exactly these two names — do not "tidy" them into
     * one.
     */
    get panelOpen() {
      return openToken !== null;
    },
    /** @returns {boolean} A call is on screen — likewise. */
    get inCall() {
      return callToken !== null;
    },
    /** @returns {string} The open panel's tab. */
    get view() {
      return view;
    },

    /**
     * @returns {{waiting: number, logged: number, missed: number, open: boolean,
     *   inCall: boolean, view: string, shot: number, shots: number, rang: number,
     *   answered: number, ignored: number, orders: number, status: string,
     *   rows: number}} A plain object for `Gameplay.stats()`, `DebugPanel` and
     *   the suites.
     */
    stats() {
      return {
        waiting: queue.length,
        logged: log.length,
        missed: log.reduce((n, c) => n + (c.missed ? 1 : 0), 0),
        open: openToken !== null,
        inCall: callToken !== null,
        view,
        shot: callRec ? shotIdx : -1,
        shots: callRec ? callRec.shots.length : 0,
        rang,
        answered,
        ignored,
        orders,
        status: statusOf(),
        rows: rows().length,
      };
    },

    /**
     * Idempotent. ⚠ IT CLOSES AND CANCELS BEFORE IT LATCHES, and in that order:
     * an overlay left on screen over a torn-down world is the one teardown bug
     * the player cannot recover from — a still image over a frozen loop with
     * nothing left to answer it (`ui/hud/Cutscene.jsx` found that the hard way).
     * The ring is taken down too, for the same reason: a HUD badge that outlives
     * the level it belongs to is a badge nothing will ever clear.
     *
     * Afterwards every method is a no-op, the subscriptions are gone and nothing
     * more is published — a late `'mission:offer'` cannot queue into a phone
     * that is on its way out.
     */
    dispose() {
      if (disposed) return;
      close();
      endCallNow(true);
      if (queue.length) {
        queue.length = 0;
        emitRing();
      }
      disposed = true;
      for (const off of unsubs) {
        try {
          off();
        } catch {
          /* a bus that tore itself down first is not our problem */
        }
      }
      unsubs.length = 0;
    },
  };

  // ── the bus: this file owns BOTH ends ──────────────────────────────────────
  if (bus?.on) {
    /**
     * @param {string} type @param {Function} fn
     * `createBus().on` returns its own unsubscriber; the `off(type, fn)`
     * fallback is for a duck-typed bus in a suite that does not.
     */
    const sub = (type, fn) => {
      const off = bus.on(type, fn);
      unsubs.push(typeof off === 'function' ? off : () => bus.off?.(type, fn));
    };

    // ── ENGINE SIDE — exactly two, and this is the whole ring mechanism ──
    sub('mission:offer', (p) => {
      if (disposed) return;
      const rec = callOf(p);
      if (!rec) return; // no call, not critical, or malformed — see `callOf`
      queue.push(rec);
      rang++;
      emitRing();
    });
    // ⚠ Cheap, and the safety net if the freeze is ever lifted: a call on screen
    // over a death splash is a conversation with a corpse. It is NOT re-queued —
    // see `endCallNow`.
    sub('player:wasted', () => {
      if (disposed) return;
      endCallNow(true);
    });

    // ── UI SIDE — the reverse channels ──
    sub('phone:show', () => {
      if (!disposed) open();
    });
    sub('phone:answer', () => {
      if (!disposed) api.answer();
    });
    /**
     * ⚠ NO TOKEN, and there is none to want: the IGNORE button lives on the ring
     * BANNER, which is non-modal HUD chrome rather than an overlay. `ignore()`'s
     * own "is there a head to take" is the whole gate.
     */
    sub('phone:ignore', () => {
      if (!disposed) api.ignore();
    });
    sub('phone:call:next', (p) => {
      api.next(typeof p === 'string' ? p : p?.token);
    });
    /**
     * The overlay's own end. We hear only SOMEBODY ELSE's — `endCallNow` clears
     * the token before it emits, so ours is already stale by the time it
     * arrives here.
     */
    sub('phone:call:end', (p) => {
      api.endCall(typeof p === 'string' ? p : p?.token);
    });
    sub('phone:view', (p) => {
      if (disposed || !openToken || p?.token !== openToken) return;
      api.setView(p?.view);
    });
    sub('phone:order', (p) => {
      if (disposed || !openToken || p?.token !== openToken) return;
      if (!str(p?.id)) return;
      api.order(p.id);
    });
    sub('phone:callback', (p) => {
      if (disposed || !openToken || p?.token !== openToken) return;
      if (!str(p?.id)) return;
      api.callBack(p.id);
    });
    sub('phone:close', (p) => {
      const token = typeof p === 'string' ? p : p?.token;
      if (disposed || !openToken || token !== openToken) return;
      close();
    });
    /**
     * ⚠ THE PULL. The bus has no replay and the HUD mounts after the level is
     * built, so a component that only listened would render a phone with no ring
     * state for ever. Straight to the emit, never a flag — there is no step here
     * to drain one, and the caller may well be a frozen HUD.
     */
    sub('phone:request', () => {
      if (disposed) return;
      emitRing(true);
      publishState();
    });
  }

  // The HUD's first paint depends on this — the `Wallet.js` rule. A phone that
  // only spoke when a job came up would leave the badge blank for the whole
  // opening walk.
  emitRing(true);

  return api;
}
